import React from 'react'
import { Check, CheckCircle } from '@phosphor-icons/react'
import { Icon } from '@shopflo/ui'
import { AppItemStatus } from 'src/lib/types/appsIntegrations'
import { classNames } from 'src/lib/utils/helpers'

export type StatusBadgeVariant = 'installed' | 'pre-installed' | 'configured' | 'active' | 'inactive' | 'free-trial'

interface StatusBadgeProps {
  variant: StatusBadgeVariant
  className?: string
}

const VARIANT_STYLES: Record<StatusBadgeVariant, { wrapper: string; label: string; icon: 'check' | 'dot' | 'none' }> = {
  installed: {
    wrapper: 'bg-emerald-100 text-emerald-700',
    label: 'Installed',
    icon: 'check'
  },
  'pre-installed': {
    wrapper: 'bg-emerald-100 text-emerald-700',
    label: 'Pre-Installed',
    icon: 'check'
  },
  configured: {
    wrapper: 'bg-yay-light text-yay-dark',
    label: 'Configured',
    icon: 'check'
  },
  active: {
    wrapper: 'bg-emerald-100 text-emerald-700',
    label: 'Active',
    icon: 'dot'
  },
  inactive: {
    wrapper: 'bg-zinc-100 text-zinc-500',
    label: 'Inactive',
    icon: 'dot'
  },
  'free-trial': {
    wrapper: 'bg-zinc-100 text-zinc-700',
    label: 'Free trial',
    icon: 'none'
  }
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ variant, className }) => {
  const { wrapper, label, icon } = VARIANT_STYLES[variant]
  return (
    <span
      className={classNames(
        'inline-flex w-fit items-center rounded-sm px-1 text-text-xs-medium',
        wrapper,
        className ?? ''
      )}
    >
      {icon === 'check' && <Icon IconComponent={Check} size='xxs' iconProps={{ weight: 'bold' }} />}
      {icon === 'dot' && (
        <span
          className={classNames(
            'w-1.5 h-1.5 rounded-full flex-shrink-0',
            variant === 'active' ? 'bg-emerald-600' : 'bg-zinc-400'
          )}
        />
      )}
      <div className='px-0.5'>
        {label}
      </div>
    </span>
  )
}

interface ItemStatusBadgeProps {
  status: AppItemStatus
  hasFreeTrial?: boolean
  autoInstall?: boolean
}

export const ItemStatusBadge: React.FC<ItemStatusBadgeProps> = ({ status, hasFreeTrial, autoInstall }) => {
  if (autoInstall || status === 'active') {
    const label = autoInstall ? 'Pre-Installed' : 'Installed'
    return (
      <span className='inline-flex w-fit items-center text-text-xs-medium text-emerald-700'>
        <Icon IconComponent={CheckCircle} size='xxs' iconProps={{ weight: 'fill' }} className='text-emerald-600' />
        <div className='px-0.5'>
          {label}
        </div>
      </span>
    )
  }
  if (status === 'configured') return <StatusBadge variant='configured' />
  if (hasFreeTrial) return <StatusBadge variant='free-trial' />
  return null
}

export default StatusBadge
